// useOrderForm.js
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useOrderServiceTypes } from "../../hooks";
import { useOrderFormLogic } from "./useOrderFormLogic";
import { getRecord } from "../../api/server";
import { usePermission } from "../../hooks/accessControl";
import { loadOrder, updateChangedFormItems, reset } from "./OrderDetailsSlice";
import { getChangedItems } from "../../components/util/helpers";
import { createRecord, commitChanges } from "./OrderDetailsSlice";
import useOrderState from "./useOrderState";
import { isAllowedFeature } from "../organization/organizationSlice";
import { useRef } from "react";
import { useRevahaAuthorities } from "../../hooks";

var unflatten = require("flat").unflatten;

const useOrderForm = (form, type, selectedOrder, actionAfterSubmit) => {
  const { branchId } = useParams();
  const dispatch = useDispatch();
  const orderServiceTypes = useOrderServiceTypes();
  const {
    startDate,
    endDate,
    noEndDate,
    clientCategories,
    selectedClientCategory,
    setStartDate,
    setEndDate,
    setNoEndDate,
    setClientCategories,
    setSelectedClientCategory,
    handleDateFieldChanges,
    selectedClientTariffSymbol,
    setSelectedClientTariffSymbol,
    selectedRevahaAuthority,
    setSelectedRevahaAuthority,
  } = useOrderState(selectedOrder);
  const scopePrefix = useSelector((state) => state.user.scopePrefix);

  const changedItems = useSelector(
    (state) => state.orderDetails.changedItems.changed
  );
  const activeOrder = useSelector((state) => state.orderDetails.activeOrder);
  const selectedCategory = useSelector(
    (state) => state.orderDetails.selectedCategory
  );
  const changedValues = useSelector(
    (state) => state.orderDetails.changedItems.values
  );
  const selectedClient = useSelector(getSelectedClient);
  const serviceTypeID = useSelector(getServiceTypeID);
  const serviceType = getServiceType(selectedClient, serviceTypeID);
  const createLoading = useSelector(
    (state) => state.pendingActions["orderDetails/create"]
  );
  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );
  const revahaAuthorities = useRevahaAuthorities();
  const filter = {
    client_category_id: selectedCategory,
  };
  useEffect(() => {
    if (selectedOrder?.id) {
      dispatch(
        loadOrder({
          orderId: selectedOrder.id,
          patientId: selectedOrder.patient_id,
        })
      );
      return () => dispatch(reset());
    }
  }, [dispatch, selectedOrder]);

  const onClientCategoriesChanged = (data) => {
    setClientCategories(data);
  };

  const filterOrderServiceTypes = (items) => {
    const category = clientCategories.find((c) => c.id === selectedCategory);
    const isAllowedSupportingCommunity = isAllowedFeature(
      "supporting_community"
    );
    const isAllowedDayCenter = isAllowedFeature("day_center");

    if (!category) return [];

    const serviceTypeIds = new Set([
      ...(category.service_types || []).map((st) => st.id),
      ...(category.default_service_type_id
        ? [category.default_service_type_id]
        : []),
    ]);

    return items.filter((item) => {
      const isServiceTypeAllowed = serviceTypeIds.has(item.id);

      // If isAllowed is false, further filter out items where supporting_community is true
      const isSupportingCommunityAllowed =
        isAllowedSupportingCommunity ||
        (!isAllowedSupportingCommunity && !item.supporting_community);

      // If isAllowedDayCenter is false, further filter out items where day_center is true
      const isDayCenterAllowed =
        isAllowedDayCenter || (!isAllowedDayCenter && !item.day_center);

      return (
        isServiceTypeAllowed &&
        isSupportingCommunityAllowed &&
        isDayCenterAllowed
      );
    });
  };

  const filterContracts = (items) => {
    const clientContractsIds = selectedClient?.contracts
      ? selectedClient?.contracts.map((item) => item.id)
      : [];
    return items.filter((i) => clientContractsIds.includes(i.id));
  };

  /**
   * Filters client frame symbols based on the selected client's frame symbols.
   *
   * @param {Array} items - The full list of client frame symbols to filter.
   * @returns {Array} A filtered list of client frame symbols that are associated with the selected client.
   */
  const filterClientFrameSymbols = (items) => {
    // Extract the IDs of the client frame symbols associated with the selected client
    const clientFrameSymbolsIds = selectedClient?.client_frame_symbols
      ? selectedClient.client_frame_symbols.map((item) => item.id)
      : [];

    // Filter the items, keeping only those whose IDs are in the clientFrameSymbolsIds array
    return items.filter((i) => clientFrameSymbolsIds.includes(i.id));
  };

  /**
   * Filters client tariff symbols based on the selected client's tariff symbols.
   *
   * @param {Array} items - The full list of client tariff symbols to filter.
   * @returns {Array} A filtered list of client tariff symbols that are associated with the selected client.
   */
  const filterClientTariffSymbols = (items) => {
    // Extract the IDs of the client tariff symbols associated with the selected client
    const clientTariffSymbolsIds = selectedClient?.client_tariff_symbols
      ? selectedClient.client_tariff_symbols.map((item) => item.id)
      : [];

    // Filter the items, keeping only those whose IDs are in the clientTariffSymbolsIds array
    return items.filter((i) => clientTariffSymbolsIds.includes(i.id));
  };

  const handleClientTariffSymbolChange = useCallback(
    (changedValues) => {
      if (
        changedValues &&
        typeof changedValues === "object" &&
        "client_tariff_symbol_id" in changedValues
      ) {
        const newTariffSymbolId = changedValues.client_tariff_symbol_id;
        if (newTariffSymbolId && selectedClient) {
          const newTariffSymbol = (
            selectedClient.client_tariff_symbols || []
          ).find((symbol) => symbol.id === newTariffSymbolId);
          setSelectedClientTariffSymbol(newTariffSymbol || null);
        } else {
          setSelectedClientTariffSymbol(null);
        }
      }
    },
    [selectedClient, setSelectedClientTariffSymbol]
  );

  const handleRevahaAuthorityChange = useCallback(
    (changedValues) => {
      if (
        changedValues &&
        typeof changedValues === "object" &&
        "revaha_authority_id" in changedValues
      ) {
        const newAuthorityId = changedValues.revaha_authority_id;
        if (newAuthorityId) {
          const newAuthority = revahaAuthorities.find(
            (auth) => auth.id === newAuthorityId
          );
          setSelectedRevahaAuthority(newAuthority || null);
        } else {
          setSelectedRevahaAuthority(null);
        }
      }
    },
    [revahaAuthorities, setSelectedRevahaAuthority]
  );

  const onFormChange = useCallback(
    (form, changedValues, allValues) => {
      const arr = getChangedItems(
        changedValues,
        allValues,
        changedItems,
        activeOrder
      );
      dispatch(updateChangedFormItems({ changed: arr, values: allValues }));
      setStartDate(allValues.effective_from);
      setEndDate(allValues.effective_to);
      setNoEndDate(allValues.no_end_date);
      handleDateFieldChanges(changedValues, allValues, form, activeOrder);
      handleCategoryChange(changedValues, form);
      handleNoEndDateChange(changedValues, form);
      handleClientTariffSymbolChange(changedValues);
      handleRevahaAuthorityChange(changedValues);
    },
    [
      dispatch,
      changedItems,
      activeOrder,
      clientCategories,
      setStartDate,
      setEndDate,
      setNoEndDate,
      handleDateFieldChanges,
      setSelectedClientTariffSymbol,
      handleClientTariffSymbolChange,
      handleRevahaAuthorityChange,
    ]
  );

  const handleCategoryChange = (changedValues, form) => {
    if (changedValues["client.category_id"]) {
      const category = clientCategories.find(
        (i) => i.id === changedValues["client.category_id"]
      );
      setSelectedClientCategory(category);
      if (category) {
        form.setFieldsValue({
          order_service_type_id: category.default_service_type_id,
        });
      }
    }
  };

  const handleNoEndDateChange = (changedValues, form) => {
    if (changedValues.no_end_date) {
      form.setFieldsValue({
        effective_to: null,
        order_close_reason_id: null,
      });
    }
  };

  const getServiceTypeLabel = () => {
    if (!serviceType) return "max_hours";
    if (!serviceType.recurring) return "max_hours";
    if (serviceType.time_frame === 0) return "hours_per_week";
    return "hours_per_month";
  };

  const orderIsUsed =
    activeOrder &&
    type !== "duplicate" &&
    (activeOrder.has_events ||
      (activeOrder.recurrent_work_events &&
        activeOrder.recurrent_work_events.length > 0));

  const isTrom =
    selectedClient &&
    selectedClient.category &&
    selectedClient.category.is_trom === true;
  const isMentoring = selectedClientCategory?.name === "משרד הביטחון חונכות"; //temporary
  const hasEditorPermission = usePermission(branchId, "orders", "retro_update");
  const { disabledDate, disabledFromDate, isSupportingCommunity, isDayCenter } =
    useOrderFormLogic({
      type,
      activeOrder,
      orderServiceTypes,
      form,
      onFormChange,
      setStartDate,
      setEndDate,
      setNoEndDate,
      selectedClient,
      selectedCategory,
      startDate,
      endDate,
      changedValues,
    });

  const onFinish = async (values) => {
    try {
      let action;
      const isEditing = type === "edit";
      const isDuplicating = type === "duplicate";

      if (isEditing) {
        action = await dispatch(commitChanges());
      } else {
        const processedValues = processFormValues(
          values,
          isDuplicating,
          activeOrder,
          activePatient
        );
        action = await dispatch(
          createRecord({
            order: processedValues,
            branchId: activePatient.branch,
            patientId: activePatient.id,
          })
        );
      }

      if (!action.error) {
        form.resetFields();
        actionAfterSubmit();
      }
    } catch (error) {
      console.error("Failed to finish form:", error);
      // Handle or log the error appropriately
    }
  };

  const processFormValues = (
    values,
    isDuplicating,
    activeOrder,
    activePatient
  ) => {
    const deepValues = unflatten(values);
    deepValues.patient = activePatient.id;
    deepValues.client = deepValues.client_id;
    deepValues.id = activeOrder.id;
    deepValues.effective_to = values.no_end_date
      ? null
      : deepValues.effective_to;
    deepValues.closed_previous_order_id =
      isDuplicating && values.closed_previous_order_id ? activeOrder.id : null;
    deepValues.associate_previous_events =
      isDuplicating && values.closed_previous_order_id
        ? values.associate_previous_events
        : false;
    return deepValues;
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    setSelectedClientTariffSymbol(activeOrder?.client_tariff_symbol);
  }, [activeOrder, setSelectedClientTariffSymbol]);

  return {
    activeOrder,
    createLoading,
    onFinish,
    onFinishFailed,
    onFormChange,
    disabledFromDate,
    disabledDate,
    isSupportingCommunity,
    isDayCenter,
    clientCategories,
    orderIsUsed,
    onClientCategoriesChanged,
    filter,
    selectedClient,
    hasEditorPermission,
    getServiceTypeLabel,
    serviceType,
    startDate,
    noEndDate,
    isTrom,
    filterOrderServiceTypes,
    filterContracts,
    filterClientFrameSymbols,
    filterClientTariffSymbols,
    changedValues,
    scopePrefix,
    activePatient,
    isMentoring,
    selectedClientTariffSymbol,
    selectedRevahaAuthority,
  };
};

const getSelectedClient = (state) => {
  const { changedItems: changed, activeOrder } = state.orderDetails;
  const { clientsByID } = state.orderDetails;
  const changedClient = clientsByID[changed.values["client_id"]];
  return changedClient || activeOrder.client;
};

const getServiceTypeID = (state) => {
  const { changedItems: changed } = state.orderDetails;
  const { activeOrder } = state.orderDetails;
  return changed.values["service_type"] || activeOrder.service_type;
};

const getServiceType = (selectedClient, serviceTypeID) => {
  if (!selectedClient) return null;
  return (
    selectedClient.service_types.find((s) => s.id === serviceTypeID) ||
    selectedClient.service_types[0]
  );
};

export default useOrderForm;
